import React from "react";
import { toast } from "react-toastify";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import {
  DragDropContext,
  Droppable,
  Draggable,
} from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";

import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";

import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_update_check_in_template_section,
  API_create_check_in_template_section,
  API_create_check_in_template_field,
  API_update_check_in_template_field,
  API_delete_check_in_template_field,
  API_reorder_check_in_template_fields,
} from "../../../constants";

import CheckInTemplateFieldDrawer from "./CheckInTemplateFieldDrawer";


const FIELD_TYPE_LABELS = {
  text: "Text",
  scale: "Scale",
  yes_no: "Yes/No",
  single_select: "Single-select",
};

const DEFAULT_CONFIG_BY_TYPE = {
  text: { rows: 4 },
  scale: {
    min: 1,
    max: 10,
    anchor_low: "",
    anchor_high: "",
    low_threshold: null,
  },
  yes_no: {},
  single_select: { options: ["Option 1", "Option 2"] },
};


function CheckInTemplateSectionDrawer(props) {
  const {
    open,
    onClose,
    section,
    template,
    homeCompany,
    onTemplateChange,
    isCreating,
  } = props;

  const [titleDraft, setTitleDraft] = React.useState(section?.title || "");
  const [promptDraft, setPromptDraft] = React.useState(section?.prompt_help_text || "");
  const [actionError, setActionError] = React.useState(null);
  // Field drawer state: editingFieldUuid set → edit mode for that field
  // (backend uuid OR draft-local uuid). creatingField=true → create mode.
  const [editingFieldUuid, setEditingFieldUuid] = React.useState(null);
  const [creatingField, setCreatingField] = React.useState(false);
  // Per-mode save state; both modes now use an explicit footer Save.
  const [isSavingCreate, setIsSavingCreate] = React.useState(false);
  const [isSavingEdit, setIsSavingEdit] = React.useState(false);
  // Local field drafts collected during section-create mode. Each entry has
  // the same shape as a backend field minus conditional fields (which are
  // gated until after the section exists). uuid is a local id used only for
  // React keys + reorder/delete; POST'd sequentially in handleSaveCreate.
  const [draftFields, setDraftFields] = React.useState([]);

  // Reset local draft state on every open / section-or-mode change so that
  // a previous session's uncommitted edits don't bleed into a new one.
  React.useEffect(() => {
    if (!open) return;
    if (isCreating) {
      setTitleDraft("");
      setPromptDraft("");
      setDraftFields([]);
    } else if (section) {
      setTitleDraft(section.title);
      setPromptDraft(section.prompt_help_text || "");
    }
    setActionError(null);
    setEditingFieldUuid(null);
    setCreatingField(false);
  }, [open, isCreating, section?.uuid]);

  if (!section && !isCreating) {
    // Don't render a hollow drawer when nothing's being edited or created.
    return null;
  }

  // Returns true on success, false on failure. Callers use this to decide
  // whether to advance their "last saved" checkpoint — advancing optimistically
  // would silently no-op a retry when the user blurs the same text again.
  const updateSection = async (changes) => {
    try {
      const res = await postRightworkAPI(API_update_check_in_template_section, {
        company_id: homeCompany,
        check_in_template_section_uuid: section.uuid,
        ...changes,
      });
      if (res.status === 200) {
        onTemplateChange(res.data?.redux?.payload?.check_in_template);
        return true;
      }
      return false;
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't save section.");
      return false;
    }
  };

  // Edit-mode handler: PATCH title/prompt if either changed, then close.
  // No-op (just close) when nothing changed so we don't bother the backend
  // and we don't bump updated_at for a no-op save.
  const handleSaveEdit = async () => {
    const trimmedTitle = titleDraft.trim();
    if (!trimmedTitle) {
      setActionError("Section title is required.");
      return;
    }
    const titleChanged = trimmedTitle !== (section?.title || "");
    const promptChanged = promptDraft !== (section?.prompt_help_text || "");
    if (!titleChanged && !promptChanged) {
      onClose();
      return;
    }
    const changes = {};
    if (titleChanged) changes.title = trimmedTitle;
    if (promptChanged) changes.prompt_help_text = promptDraft;

    setIsSavingEdit(true);
    setActionError(null);
    try {
      const ok = await updateSection(changes);
      if (ok) {
        onClose();
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Create-mode handler: POST the new section, then POST each draft field
  // in order. Closes the drawer on completion or partial failure. State is
  // hoisted outside the try so the finally block can always sync the
  // latest server-truth template to the parent when the section actually
  // made it to the backend — without that sync, a thrown error after
  // section creation would leave the new section invisible in local state
  // and a retry would create a duplicate. Each field POST gets its own
  // inner try/catch so thrown axios errors collapse into the same
  // failed-field path as non-200 responses.
  const handleSaveCreate = async () => {
    const trimmedTitle = titleDraft.trim();
    if (!trimmedTitle) {
      setActionError("Section title is required.");
      return;
    }
    const existingUuids = new Set((template.sections || []).map((s) => s.uuid));
    setIsSavingCreate(true);
    setActionError(null);

    let updatedTemplate = null;
    let sectionCreated = false;
    let failedFieldLabel = null;

    try {
      // 1) Section.
      const sectionRes = await postRightworkAPI(
        API_create_check_in_template_section,
        {
          company_id: homeCompany,
          check_in_template_uuid: template.uuid,
          title: trimmedTitle,
          prompt_help_text: promptDraft || "",
        },
      );
      if (sectionRes.status !== 200) {
        setActionError("Couldn't create section.");
        return;
      }
      updatedTemplate = sectionRes.data?.redux?.payload?.check_in_template;
      sectionCreated = true;
      const newSection = (updatedTemplate?.sections || []).find(
        (s) => !existingUuids.has(s.uuid),
      );
      if (!newSection) {
        // Backend returned 200 but we can't locate the new section — let the
        // finally block sync what we have and close anyway.
        return;
      }

      // 2) Each draft field, sequentially. Drafts hold the full payload
      //    (label, help_text, field_type, config) collected via the field
      //    drawer. Conditional rules are not included — they can only be
      //    set after the section + sibling fields exist, so the field
      //    drawer suppresses that UI in section-create mode. Wrap each
      //    POST in its own try/catch so a thrown error (network / 5xx)
      //    takes the same partial-failure path as a non-200 response.
      for (const draftField of draftFields) {
        try {
          const fieldRes = await postRightworkAPI(
            API_create_check_in_template_field,
            {
              company_id: homeCompany,
              check_in_template_section_uuid: newSection.uuid,
              label: draftField.label,
              help_text: draftField.help_text || "",
              field_type: draftField.field_type,
              config: draftField.config || DEFAULT_CONFIG_BY_TYPE[draftField.field_type] || {},
            },
          );
          if (fieldRes.status === 200) {
            updatedTemplate = fieldRes.data?.redux?.payload?.check_in_template;
          } else {
            failedFieldLabel = draftField.label;
            break;
          }
        } catch (fieldErr) {
          failedFieldLabel = draftField.label;
          break;
        }
      }
    } catch (e) {
      // Only reached when the section POST itself throws (sectionCreated
      // is still false). The finally below leaves local state untouched.
      setActionError(
        e?.response?.data?.system?.message || "Couldn't create section.",
      );
    } finally {
      setIsSavingCreate(false);
      if (sectionCreated && updatedTemplate) {
        // Always sync when the section made it to the backend — covers full
        // success, partial-field failure, and the "can't find new section"
        // edge case. Without this, local state would silently disagree with
        // the backend and a retry would duplicate-create.
        onTemplateChange(updatedTemplate);
        if (failedFieldLabel) {
          // setActionError is invisible past the close below; use a toast
          // so the partial-failure message actually reaches the user.
          toast.error(
            `Section saved, but couldn't add field "${failedFieldLabel}". Reopen the section to retry.`,
          );
        }
        onClose();
      }
    }
  };

  // Field drawer is controlled — it builds the fieldData and we handle
  // persistence here. Four cases, parameterized by (isCreating × editingFieldUuid):
  //   - create section + no field uuid     → append to draftFields
  //   - create section + draft field uuid  → replace in draftFields
  //   - edit section   + no field uuid     → POST create against section.uuid
  //   - edit section   + backend field uuid → POST update against field uuid
  // Throws so the field drawer can surface the error in its own state.
  const handleFieldDrawerSubmit = async (fieldData) => {
    if (isCreating) {
      if (editingFieldUuid) {
        setDraftFields((prev) =>
          prev.map((f) =>
            f.uuid === editingFieldUuid ? { ...fieldData, uuid: f.uuid } : f,
          ),
        );
      } else {
        setDraftFields((prev) => [
          ...prev,
          {
            ...fieldData,
            uuid: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          },
        ]);
      }
      return;
    }

    if (editingFieldUuid) {
      const res = await postRightworkAPI(API_update_check_in_template_field, {
        company_id: homeCompany,
        check_in_template_field_uuid: editingFieldUuid,
        ...fieldData,
      });
      if (res.status !== 200) {
        throw new Error("Couldn't save field.");
      }
      onTemplateChange(res.data?.redux?.payload?.check_in_template);
    } else {
      const res = await postRightworkAPI(API_create_check_in_template_field, {
        company_id: homeCompany,
        check_in_template_section_uuid: section.uuid,
        ...fieldData,
      });
      if (res.status !== 200) {
        throw new Error("Couldn't create field.");
      }
      onTemplateChange(res.data?.redux?.payload?.check_in_template);
    }
  };

  const openCreateFieldDrawer = () => {
    setEditingFieldUuid(null);
    setCreatingField(true);
  };

  const closeFieldDrawer = () => {
    setEditingFieldUuid(null);
    setCreatingField(false);
  };

  const handleDeleteField = async (fieldUuid, fieldLabel) => {
    if (!window.confirm(`Delete field "${fieldLabel}"?`)) return;
    // Create mode: just splice from local drafts.
    if (isCreating) {
      setDraftFields((prev) => prev.filter((f) => f.uuid !== fieldUuid));
      return;
    }
    try {
      const res = await postRightworkAPI(API_delete_check_in_template_field, {
        company_id: homeCompany,
        check_in_template_field_uuid: fieldUuid,
      });
      if (res.status === 200) {
        onTemplateChange(res.data?.redux?.payload?.check_in_template);
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't delete field.");
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    // Create mode: local reorder only.
    if (isCreating) {
      const reordered = Array.from(draftFields);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);
      setDraftFields(reordered);
      return;
    }

    const reordered = Array.from(section.fields);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    // Optimistically reorder fields inside this section in the parent template.
    onTemplateChange({
      ...template,
      sections: template.sections.map((s) =>
        s.uuid === section.uuid ? { ...s, fields: reordered } : s,
      ),
    });

    try {
      const res = await postRightworkAPI(API_reorder_check_in_template_fields, {
        company_id: homeCompany,
        check_in_template_section_uuid: section.uuid,
        ordered_uuids: reordered.map((f) => f.uuid),
      });
      if (res.status === 200) {
        onTemplateChange(res.data?.redux?.payload?.check_in_template);
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't save field order.");
      onTemplateChange(template);
    }
  };

  // Resolve the editing field from either the backend section or the local
  // drafts. In create mode there is no backend section yet, so look in
  // draftFields. In edit mode, look in section.fields.
  const editingField = (() => {
    if (!editingFieldUuid) return null;
    if (isCreating) {
      return draftFields.find((f) => f.uuid === editingFieldUuid) || null;
    }
    return (section?.fields || []).find((f) => f.uuid === editingFieldUuid) || null;
  })();

  const headerTitle = isCreating
    ? "New section"
    : `Edit section: ${section.title}`;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="w-[560px] max-w-screen h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader title={headerTitle} onClose={onClose} />

          <div className="px-6 py-4 space-y-5">
            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Section title
              </label>
              <TextField
                value={titleDraft}
                onChange={(evt) => setTitleDraft(evt.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    fontSize: "14px",
                    borderRadius: "8px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>

            {/* Prompt help text */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Prompt (shown to the manager during the check-in)
              </label>
              <TextField
                value={promptDraft}
                onChange={(evt) => setPromptDraft(evt.target.value)}
                multiline
                rows={6}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="The questions you want to spark conversation about in this section."
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    fontSize: "13px",
                    borderRadius: "8px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>

            {/* Fields — works in both create and edit modes. In create mode
                the list comes from local drafts and add/delete/reorder all
                run locally; on Save they're POST'd in order. The Edit pencil
                is hidden in create mode because the per-field config drawer
                requires a backend uuid. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-900">Fields</h3>
                <span className="text-xs text-slate-500">
                  {isCreating
                    ? "Add fields below — they'll be created with the section."
                    : "Drag to reorder"}
                </span>
              </div>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable
                  droppableId={isCreating ? "fields-draft" : `fields-${section.uuid}`}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-2"
                    >
                      {(isCreating ? draftFields : section.fields || []).map(
                        (field, idx) => (
                        <Draggable
                          key={field.uuid}
                          draggableId={field.uuid}
                          index={idx}
                        >
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={`bg-white border rounded-lg p-2 flex items-center gap-2 ${
                                snapshot.isDragging
                                  ? "border-violet-400 shadow-md"
                                  : "border-slate-200"
                              }`}
                            >
                              <div
                                {...dragProvided.dragHandleProps}
                                className="text-slate-400 hover:text-slate-600 cursor-grab"
                              >
                                <DragIndicatorIcon style={{ fontSize: "18px" }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-900 truncate">
                                    {field.label}
                                  </span>
                                  <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                    {FIELD_TYPE_LABELS[field.field_type] || field.field_type}
                                  </span>
                                  {field.conditional_on_field_uuid && (
                                    <span className="text-xs bg-violet-50 border border-violet-200 text-violet-700 px-1.5 py-0.5 rounded">
                                      conditional
                                    </span>
                                  )}
                                </div>
                              </div>
                              {/* Edit pencil — works for both backend fields
                                  and local drafts; the field drawer is
                                  controlled (no backend uuid needed). */}
                              <Tooltip title="Edit field" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setCreatingField(false);
                                    setEditingFieldUuid(field.uuid);
                                  }}
                                >
                                  <EditIcon style={{ fontSize: "16px" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete field" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteField(field.uuid, field.label)}
                                >
                                  <DeleteOutlinedIcon
                                    style={{ fontSize: "16px" }}
                                    className="text-slate-500 hover:text-rose-600"
                                  />
                                </IconButton>
                              </Tooltip>
                            </div>
                          )}
                        </Draggable>
                        ),
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Create field — opens the field drawer for full
                  configuration (label, type, type-specific config, and
                  conditional rules in edit mode). Same slate dashed
                  pattern as "Add a section" for visual consistency. */}
              <div
                role="button"
                tabIndex={0}
                onClick={openCreateFieldDrawer}
                onKeyDown={(evt) => {
                  if (evt.key === "Enter" || evt.key === " ") {
                    evt.preventDefault();
                    openCreateFieldDrawer();
                  }
                }}
                className="w-full mt-2 py-3 border border-dashed border-slate-300 rounded-xl flex items-center justify-center cursor-pointer hover:bg-slate-50 hover:border-slate-400 transition-colors"
              >
                <AddIcon
                  style={{
                    fontSize: "16px",
                    color: "#64748B",
                    marginRight: "4px",
                  }}
                />
                <p className="text-sm text-slate-500">Create field</p>
              </div>
            </div>

            {actionError && (
              <p className="text-sm text-rose-600">{actionError}</p>
            )}
          </div>

          {/* Bottom spacer — clears the fixed 150px DrawerFooterContainerLarge
              which is rendered in both create and edit modes. */}
          <div className="h-[200px]"></div>
        </div>

        {/* Footer — same shape in both modes (DrawerFooterContainerLarge +
            LoadingButton primary + ButtonOutlined secondary), only the
            handler + button label differ. Matches the pattern used in
            CertificateDrawer / ShiftDropDrawer. */}
        <DrawerFooterContainerLarge>
          <div className="w-full">
            <LoadingButton
              variant="contained"
              loading={isCreating ? isSavingCreate : isSavingEdit}
              loadingPosition="end"
              endIcon={<SaveIcon />}
              onClick={isCreating ? handleSaveCreate : handleSaveEdit}
              sx={{
                width: "100%",
                textTransform: "none",
                borderRadius: "12px",
                paddingY: "14px",
                backgroundColor: "#6d28d9",
                "&:hover": { backgroundColor: "#5b21b6" },
                fontSize: "16px",
              }}
            >
              {isCreating ? "Save Section" : "Save Changes"}
            </LoadingButton>
          </div>
          <div className="w-full mt-3">
            <ButtonOutlined text="Cancel" onClick={onClose} />
          </div>
        </DrawerFooterContainerLarge>

        {/* Nested field drawer — controlled. We hand it onSubmit so it can
            persist either to the backend or to local drafts depending on
            the section's mode. hideConditional gates the conditional UI in
            section-create mode (drafts have no stable cross-API uuids).
            template is augmented in create mode with a synthetic section
            holding the draftFields so future cross-section conditional
            references would resolve correctly — even though we currently
            hide that UI, it leaves the door open. */}
        <CheckInTemplateFieldDrawer
          open={!!editingField || creatingField}
          onClose={closeFieldDrawer}
          field={editingField}
          template={template}
          onSubmit={handleFieldDrawerSubmit}
          hideConditional={isCreating}
        />
      </div>
    </Drawer>
  );
}

export default CheckInTemplateSectionDrawer;
